import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import Navbar from "@/components/site/Navbar";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import Manifesto from "@/components/site/Manifesto";
import InteractiveDemo from "@/components/site/InteractiveDemo";
import BeforeAfter from "@/components/site/BeforeAfter";
import Services from "@/components/site/Services";
import Industries from "@/components/site/Industries";
import AiEmployee from "@/components/site/AiEmployee";
import HowItWorks from "@/components/site/HowItWorks";
import RoiCalculator from "@/components/site/RoiCalculator";
import Trust from "@/components/site/Trust";
import Testimonials from "@/components/site/Testimonials";
import Pricing from "@/components/site/Pricing";
import Faq from "@/components/site/Faq";
import FinalCta from "@/components/site/FinalCta";
import Footer from "@/components/site/Footer";
import { PrivacyPolicy, TermsAndConditions } from "@/components/site/Legal";
import ServicePage from "@/components/site/ServicePage";
import { BlogIndex, BlogPost } from "@/components/site/Blog";
import { Seo, ORGANIZATION_LD, WEBSITE_LD } from "@/components/site/Seo";
import { SERVICE_PAGES } from "@/data/services";
import { FAQS } from "@/data/content";

const HOME_FAQ_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const Home = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 300);
            return () => clearTimeout(t);
        }
    }, [hash]);
    return (
        <main>
            <Seo
                title="AI Employee & Business Automation for Service Businesses | Flowtech"
                description="Flowtech builds AI employees and automation systems for service businesses across the US — instant lead response, appointment scheduling, follow-up, and CRM automation. From $299/month, no setup fee."
                path="/"
                jsonLd={[ORGANIZATION_LD, WEBSITE_LD, HOME_FAQ_LD]}
            />
            <Hero />
        <Marquee />
        <Manifesto />
        <InteractiveDemo />
        <BeforeAfter />
        <Services />
        <Industries />
        <AiEmployee />
        <HowItWorks />
        <RoiCalculator />
        <Trust />
        <Testimonials />
        <Pricing />
        <Faq />
        <FinalCta />
        </main>
    );
};

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

function App() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, anchors: { offset: -90 } });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="App text-slate-200 overflow-x-clip">
            <div className="noise" />
            <BrowserRouter>
                <ScrollToTop />
                <Navbar />
                <Routes>
                    <Route path="/" element={<Home />} />
                    {SERVICE_PAGES.map((s) => (
                        <Route key={s.slug} path={`/${s.slug}`} element={<ServicePage data={s} />} />
                    ))}
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route
                        path="/privacy-policy"
                        element={
                            <>
                                <Seo
                                    title="Privacy Policy | Flowtech"
                                    description="How Flowtech collects, uses, and protects your information when you use our website and AI automation services."
                                    path="/privacy-policy"
                                />
                                <PrivacyPolicy />
                            </>
                        }
                    />
                    <Route
                        path="/terms-and-conditions"
                        element={
                            <>
                                <Seo
                                    title="Terms & Conditions | Flowtech"
                                    description="The terms and conditions for using the Flowtech website and AI automation services, governed by the laws of the Republic of the Philippines."
                                    path="/terms-and-conditions"
                                />
                                <TermsAndConditions />
                            </>
                        }
                    />
                </Routes>
                <Footer />
            </BrowserRouter>
        </div>
    );
}

export default App;
